import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FlatButton } from '@framework/ui/atoms';
import { Modal } from '@framework/ui/elements';
import { useNavigate } from 'react-router';
import { UserPagePathBuilder } from '../UserPagePathBuilder';
import { trpcClient } from '@framework/trpcClient';

type Props = {
  isOpen: boolean;
  onClose(): void;
};

export const UserSignUpRequestWithEmailModal: React.FC<Props> = ({ isOpen, onClose }: Props) => {
  const { t } = useTranslation();
  const [processing, setProcessing] = useState<boolean>(false);
  const [error, setError] = useState('');

  const [name, setName] = useState('');
  const [mailAddress, setMailAddress] = useState('');

  const navigate = useNavigate();

  const handleChangeMailAddress = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setMailAddress(event.target.value);
  }, []);

  const handleChangeName = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
  }, []);

  const handleClose = useCallback(() => {
    setProcessing(false);
    onClose();
    navigate(UserPagePathBuilder.entryPage());
  }, [navigate, onClose]);

  const handleSubmit = useCallback(async () => {
    setProcessing(true);

    try {
      const { status } = await trpcClient.user.auth.sendSignUpRequestToken.mutate({ name, mailAddress });
      if (status === 'ok') {
        navigate(UserPagePathBuilder.signUpRequestDonePage());
      } else {
        setError(t('home.signUpRequest.errorDuplicate'));
      }
    } catch (error) {
      console.error(error);
      setError(t('home.signUpRequest.errorFailed'));
    } finally {
      setProcessing(false);
    }
  }, [navigate, mailAddress, name, t]);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} className="flex flex-col">
      {/* header */}
      <div className="px-8 pb-4 pt-8 text-2xl font-bold">{t('home.signUpRequest.title')}</div>
      <hr className="w-full" />

      {/* content */}
      <div className="flex flex-col p-8">
        <div className="pb-2 font-bold">{t('home.signUpRequest.emailLabel')}</div>
        <input
          type="text"
          className="rounded-sm border p-2"
          value={mailAddress}
          onChange={handleChangeMailAddress}
          autoFocus
        />

        <div className="pb-2 pt-8 font-bold">{t('home.signUpRequest.nameLabel')}</div>
        <input type="text" className="rounded-sm border p-2" value={name} onChange={handleChangeName} />

        {error && <div className="pt-8 text-red-700">{error}</div>}
      </div>

      {/* footer */}
      <hr className="w-full" />
      <div className="flex justify-end px-8 pb-8 pt-4">
        <FlatButton onClick={handleClose}>{t('common.action.close')}</FlatButton>
        <FlatButton
          color="brand"
          onClick={handleSubmit}
          className="ml-2 px-8"
          disabled={processing}
          loading={processing}
        >
          {t('home.signUpRequest.submit')}
        </FlatButton>
      </div>
    </Modal>
  );
};
